/**
 * DIS01-S 4.2: the positive receipt (XCTL03) Clinio sent for a received
 * referral must answer exactly that referral — its envelope, its letter, and
 * the referral's sender as receiver.
 */
export type CtlExchange = {
  id: string;
  kind: string;
  direction: 'in' | 'out';
  envelopeIdentifier: string | null;
  letterIdentifier: string | null;
  senderEan: string | null;
  receiverEan: string | null;
  originalEnvelopeIdentifier: string | null;
  originalLetterIdentifier: string | null;
  createdAt: string;
};

export type ReceiptMatchCheck = { id: 'kind' | 'envelope' | 'letter' | 'receiver' | 'sender'; pass: boolean; expected: string; actual: string };

export type ReferralReceipt = { receipt: CtlExchange; checks: ReceiptMatchCheck[]; matches: boolean };

/** The imported referral's own exchange row: the stored exchange id, else the letter id. */
export function findReferralExchange(
  rows: readonly CtlExchange[],
  exchangeId: string | null,
  letterIdentifier: string | null,
): CtlExchange | null {
  return (
    rows.find((r) => exchangeId !== null && r.id === exchangeId) ??
    rows.find((r) => r.direction === 'in' && letterIdentifier !== null && r.letterIdentifier === letterIdentifier && !r.kind.includes('CTL')) ??
    null
  );
}

const text = (v: string | null | undefined) => (v ?? '').trim();

/** Every outgoing XCTL02/XCTL03 that answers the referral's envelope, newest first, with its checks. */
export function referralReceipts(referral: CtlExchange, rows: readonly CtlExchange[]): ReferralReceipt[] {
  const envelope = text(referral.envelopeIdentifier);
  if (!envelope) return [];
  return rows
    .filter((r) => r.direction === 'out' && (r.kind === 'XCTL03' || r.kind === 'XCTL02') && text(r.originalEnvelopeIdentifier) === envelope)
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
    .map((receipt) => {
      const checks: ReceiptMatchCheck[] = [
        { id: 'kind', pass: receipt.kind === 'XCTL03', expected: 'XCTL03', actual: receipt.kind },
        { id: 'envelope', pass: true, expected: envelope, actual: text(receipt.originalEnvelopeIdentifier) },
        ...(text(receipt.originalLetterIdentifier)
          ? [
              {
                id: 'letter' as const,
                pass: text(receipt.originalLetterIdentifier) === text(referral.letterIdentifier),
                expected: text(referral.letterIdentifier),
                actual: text(receipt.originalLetterIdentifier),
              },
            ]
          : []),
        { id: 'receiver', pass: text(receipt.receiverEan) === text(referral.senderEan), expected: text(referral.senderEan), actual: text(receipt.receiverEan) },
        { id: 'sender', pass: text(receipt.senderEan) === text(referral.receiverEan), expected: text(referral.receiverEan), actual: text(receipt.senderEan) },
      ];
      return { receipt, checks, matches: checks.every((c) => c.pass) };
    });
}
